import MiniSearch from 'minisearch'
import { Package } from '../config/api'

// Strip HTML tags from indexed text (descriptions/intros contain markup)
const stripHtml = (value: string | null | undefined): string => {
  return value ? value.replace(/<[^>]*>/g, ' ') : ''
}

/**
 * Rank packages against a free-text search term using client-side full-text search.
 *
 * Indexes name, intro, description and location (destination name) with field boosts,
 * prefix matching and typo tolerance, so a keyword that only appears in a package's
 * description still finds that package. Returns matches ordered by relevance.
 *
 * Structured filters (franchise, category, destination, dates, price) are applied
 * BEFORE this runs - search only ranks within the already-filtered set.
 */
export function rankPackages(packages: Package[], term: string): Package[] {
  const trimmed = term.trim()
  if (!trimmed) {
    return packages
  }

  // The API can return the same package on multiple pages - MiniSearch
  // throws on duplicate IDs, so index one occurrence per id
  const packagesById = new Map(packages.map(pkg => [pkg.id, pkg]))
  const uniquePackages = [...packagesById.values()]

  const miniSearch = new MiniSearch<Package>({
    fields: ['name', 'intro', 'description', 'location'],
    // extractField is also used for non-text fields like id, so only strip strings
    extractField: (pkg, fieldName) => {
      const value = pkg[fieldName as keyof Package]
      return typeof value === 'string' ? stripHtml(value) : value
    },
    searchOptions: {
      prefix: true,
      fuzzy: 0.2,
      boost: { name: 3, location: 2.5, intro: 1.5, description: 1 },
    },
  })
  miniSearch.addAll(uniquePackages)

  return miniSearch
    .search(trimmed)
    .map(result => packagesById.get(result.id as number))
    .filter((pkg): pkg is Package => Boolean(pkg))
}
